import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin } from 'lucide-react';
import emailjs from '@emailjs/browser';
import bandeau from '../img/bandeau.jpg';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const ContactPage = () => {
  const [formStatus, setFormStatus] = useState({ message: '', isError: false });
  const form = useRef();

  const sendEmail = (e) => {
    e.preventDefault();
    setFormStatus({ message: 'Sending...', isError: false });
    // REAL EMAILJS IMPLEMENTATION
    // 1. Create an account at emailjs.com
    // 2. Replace the 3 placeholder strings below with your actual keys
    emailjs.sendForm(
      'YOUR_SERVICE_ID',   // Replace with your EmailJS Service ID
      'YOUR_TEMPLATE_ID',  // Replace with your EmailJS Template ID
      form.current,
      'YOUR_PUBLIC_KEY'    // Replace with your EmailJS Public Key
    )
    .then((result) => {
      setFormStatus({ message: 'Message sent successfully!', isError: false });
      form.current.reset();
    }, (error) => {
      console.error(error.text);
      setFormStatus({ message: 'Failed to send message. Please try again.', isError: true });
    });
  };

  return (
    <div className="min-h-screen bg-white text-ninsun-text">
      <Navigation />
      <section className="pt-40 pb-20 relative bg-ninsun-surface border-b border-gray-200 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={bandeau} 
            alt="Ninsun Biotech Background" 
            className="w-full h-full object-cover object-center opacity-60"
          />
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm"></div>
        </div>
        <div className="container mx-auto px-6 max-w-5xl text-center relative z-10">
          <motion.h1 
            className="text-5xl md:text-6xl font-display font-bold text-ninsun-text mb-6 tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Get in touch with Us
          </motion.h1>
          <motion.div
            className="text-lg md:text-xl text-ninsun-muted leading-relaxed max-w-3xl mx-auto space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <p>
              Ninsun Biotech is based at the BioArk, Route de l'Ile-au-Bois 1A, 1870 Monthey, Valais, Switzerland and is supported by the <a href="https://bioark.ch/" target="_blank" rel="noreferrer" className="text-ninsun-gold hover:underline font-bold">Fondation The Ark</a>.
            </p>
            <p>
              Contact us by our request form below or at <a href="mailto:contact@ninsun-biotech.com" className="text-ninsun-gold hover:underline font-bold">contact@ninsun-biotech.com</a>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 relative bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-16 max-w-6xl mx-auto">
            
            {/* Contact Information */}
            <motion.div 
              className="lg:col-span-2 space-y-8"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="glass-panel p-8 rounded-2xl">
                <h3 className="text-2xl font-display font-bold text-ninsun-text mb-6 tracking-tight">Our Location</h3>
                <div className="flex items-start space-x-4 mb-6">
                  <MapPin className="w-6 h-6 text-ninsun-gold flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-ninsun-text mb-1">Switzerland Headquarters</h4>
                    <p className="text-ninsun-muted leading-relaxed">
                      BioArk<br />
                      Route de l'Ile-au-Bois 1A<br />
                      1870 Monthey<br />
                      Switzerland
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Mail className="w-6 h-6 text-ninsun-gold flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-ninsun-text mb-1">Email Us</h4>
                    <a href="mailto:contact@ninsun-biotech.com" className="text-ninsun-muted hover:text-ninsun-gold transition-colors">
                      contact@ninsun-biotech.com
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div 
              className="lg:col-span-3"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="glass-panel p-8 md:p-10 rounded-2xl">
                <form ref={form} onSubmit={sendEmail} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="user_name" className="block text-sm font-bold text-ninsun-text mb-2">Name</label>
                      <input 
                        type="text" 
                        name="user_name" 
                        id="user_name" 
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-ninsun-gold focus:ring-2 focus:ring-ninsun-gold/20 outline-none transition-all bg-ninsun-surface"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="user_email" className="block text-sm font-bold text-ninsun-text mb-2">Email</label>
                      <input 
                        type="email" 
                        name="user_email" 
                        id="user_email" 
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-ninsun-gold focus:ring-2 focus:ring-ninsun-gold/20 outline-none transition-all bg-ninsun-surface"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-bold text-ninsun-text mb-2">Subject</label>
                    <input 
                      type="text" 
                      name="subject" 
                      id="subject" 
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-ninsun-gold focus:ring-2 focus:ring-ninsun-gold/20 outline-none transition-all bg-ninsun-surface"
                      placeholder="How can we help you?"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-bold text-ninsun-text mb-2">Message</label>
                    <textarea 
                      name="message" 
                      id="message" 
                      rows="6" 
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-ninsun-gold focus:ring-2 focus:ring-ninsun-gold/20 outline-none transition-all bg-ninsun-surface resize-none"
                      placeholder="Your message here..."
                    ></textarea>
                  </div>
                  <button 
                    type="submit" 
                    className="w-full bg-ninsun-gold hover:opacity-90 text-white font-bold py-4 rounded-lg transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-md"
                  >
                    Send Message
                  </button>

                  {formStatus.message && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-lg text-center font-bold ${
                        formStatus.isError ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'
                      }`}
                    >
                      {formStatus.message}
                    </motion.div>
                  )}
                </form>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;